<template>
  <PageHeader>Settings</PageHeader>
  <div v-if="settings" class="p-12 bg-white shadow-md mb-10 max-w-7xl rounded-md">
    <h1 class="text-4xl font-bold mb-4">Account</h1>
    <div class="pb-2">Email: {{ settings.account.email }}</div>

    <h1 class="text-4xl font-bold mb-4">Workspace</h1>
    <div class="pb-2">Name: {{ settings.workspace.name }}</div>
    <div class="flex">
      <div class="pb-2 mb-4">ID: {{ settings.workspace.id }}</div>
      <ClipboardDocumentIcon @click="copyWorkspaceId" class="h-6 w-6 ml-2 cursor-pointer text-secondary" />
    </div>

    <template v-if="settings.workspace.invited_accounts.length > 0">
      <h2 class="text-2xl font-bold mb-4">Pending Invitations ({{ settings.workspace.invited_accounts.length }})</h2>
      <AccountList class="mb-4" :accounts="settings.workspace.invited_accounts" @cancel-invitation="handleCancelInvitation" />
    </template>

    <h2 class="text-2xl font-bold mb-4">Users</h2>
    <AccountList class="mb-4" :accounts="settings.workspace.accounts" :current-user-role="settings.account.role" />

    <h2 class="text-2xl font-bold mb-4">Invite users to this workspace</h2>
    <FormKit type="form" @submit="handleInvite" submit-label="Invite User">
      <div class="flex w-1/2">
        <div class="grow">
          <FormKit type="email" placeholder="foo@example.com" name="email"></FormKit>
        </div>
        <div>
          <FormKit type="select" :options="getInviteRoleOptions()" name="role" input-class="h-14"></FormKit>
        </div>
      </div>
    </FormKit>
  </div>
</template>

<script setup lang="ts">
import { fetchSettings, inviteUser, cancelInvitation } from '@/services/settingsService'
import type { FormKitNode } from '@formkit/core'
import { ClipboardDocumentIcon } from '@heroicons/vue/24/outline'

import AccountList from '../components/AccountList.vue'
import PageHeader from '../components/PageHeader.vue'
import { successNotification } from '@/utils/notifications'
import { useMutation, useQuery } from '@tanstack/vue-query'
import { useQueryClient } from '@tanstack/vue-query'
import type { WorkspaceAccount } from '../types'

const queryClient = useQueryClient()

const { data: settings } = useQuery({ queryKey: ['settings'], queryFn: fetchSettings })
const inviteAccountMutation = useMutation({
  mutationFn: (params: { email: string; role: string }) => inviteUser(params.email, params.role),
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['settings'] })
  },
})

const cancelInvitationMutation = useMutation({
  mutationFn: (id: string) => cancelInvitation(id),
  onSettled: () => {
    queryClient.invalidateQueries({ queryKey: ['settings'] })
  },
})

const handleCancelInvitation = async (account: WorkspaceAccount) => {
  if (confirm('Are you sure you want to cancel this invitation?')) {
    await cancelInvitationMutation.mutateAsync(account.id)
  }
}

const handleInvite = async (d: { email: string; role: string }, node: FormKitNode) => {
  await inviteAccountMutation.mutateAsync({ email: d.email, role: d.role })
  node.reset()
}

const copyWorkspaceId = () => {
  navigator.clipboard.writeText(settings.value?.workspace.id!)
  successNotification('Workspace ID copied to clipboard')
}

const getInviteRoleOptions = () => {
  if (settings.value?.account.role === 'owner') {
    return ['user', 'admin', 'owner']
  } else if (settings.value?.account.role === 'admin') {
    return ['user', 'admin']
  }
  return ['user']
}
</script>
